'use client';

import { cn } from '@/lib';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '.';

/**
 * MyTabs Component
 * 
 * A URL-synced tab component with optimized performance for Next.js 15+
 * 
 * Features:
 * - URL state persistence via searchParams
 * - Non-blocking navigation with useTransition
 * - Loading states during tab transitions
 * - Memoized calculations to prevent unnecessary re-renders
 * 
 * @param {Object} props - Component props
 * @param {Array} props.tabs - Array of tab objects with { id, tab_label, content }
 * @param {string} props.className - Optional className for styling
 * 
 * @example
 * <MyTabs 
 *   tabs={[
 *     { id: 1, tab_label: 'Home', content: <HomeContent /> },
 *     { id: 2, tab_label: 'Profile', content: <ProfileContent /> }
 *   ]} 
 * />
 */
export const MyTabs = ({ tabs = [], className }) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  // useTransition provides isPending state for loading indicators
  // and startTransition for non-blocking state updates
  const [isPending, startTransition] = useTransition();

  /**
   * Memoize initial tab calculation to avoid recalculation on every render
   * Priority: URL param -> First tab -> Fallback to "1"
   */
  const initialTab = useMemo(() => {
    const urlTab = searchParams.get('tab');
    return urlTab || tabs?.[0]?.id?.toString() || "1";
  }, [searchParams, tabs]);

  // Local state for active tab - updates immediately for responsive UI
  const [activeTab, setActiveTab] = useState(initialTab);

  /**
   * Sync activeTab with URL changes (browser back/forward, external URL updates)
   * Only updates if URL tab differs from current state to prevent infinite loops
   */
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams, activeTab]);

  /**
   * Handle tab change with optimized performance
   * 1. Immediate UI update (setActiveTab)
   * 2. Deferred URL update (wrapped in startTransition)
   * 
   * This approach ensures tab switching feels instant while
   * URL updates happen in the background without blocking
   */
  const handleTabChange = useCallback((value) => {
    // Immediate UI update - no delay for user
    setActiveTab(value);

    // Non-blocking URL update using Next.js 15 useTransition pattern
    startTransition(() => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("tab", value);

      // Shallow routing - updates URL without full page reload
      router.replace(`?${params.toString()}`, { scroll: false });
    });
  }, [router, searchParams]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className={cn("w-full", className)}
    >
      {/* Tab Navigation */}
      <TabsList className={cn(
        "transition-opacity duration-200",
        "setWidth no-scrollbar flex h-13 w-full max-w-full justify-center overflow-x-auto rounded-sm max-lg:items-start max-lg:justify-start",
        isPending && "opacity-50 pointer-events-none"
      )}>
        {tabs?.map((tab, i) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id.toString()}
            disabled={isPending}
            className={cn(
              "relative transition-all",
              isPending && "cursor-not-allowed"
            )}
          >
            {i + 1}. {tab.tab_label}

            {/* Optional: Loading indicator on active tab */}
            {isPending && activeTab === tab.id.toString() && (
              <span className="ml-2 inline-flex items-center">
                <svg
                  className="animate-spin h-3.5 w-3.5 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            )}


          </TabsTrigger>
        ))}
      </TabsList>

      {/* Tab Content Panels */}
      {tabs?.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id.toString()}
          className={cn(
            "transition-opacity duration-200",
            isPending && "opacity-70"
          )}
        >

          {tab?.content ?? 'No tab content available.'}
        </TabsContent>
      ))}
    </Tabs>
  );
};